import {externalGuestDisplay} from '../../features/network/guestDisplay';
import {NetworkAccounts} from '../../features/network/Network';
import CoffeeFlatControl from '../Common/CoffeeFlatControl';
import {ProfileProtectionControl} from './ProfileProtection';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {createPortal} from 'react-dom';
import axios from 'axios';
import OrderEditHistory from '../Common/OrderEditHistory';
import { useStationMode } from '../Common/useStationMode';
import BalanceDeadlineNotice from '../../features/payment/BalanceDeadlineNotice';
import {
  ArrowLeft,
  Banknote,
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Coffee,
  Cookie,
  GlassWater,
  ReceiptText,
  RotateCcw,
  UtensilsCrossed,
  WalletCards,
  X,
} from 'lucide-react';
import {
  buildDepartmentAccounts,
  calculateEmployeeOrderTotal,
  filterHistoryEntries,
  getHistoryCategories,
  getPayPalLink,
  groupHistoryEntriesByCategory,
  groupHistoryEntriesByDay,
} from './employeeHistory';

const HISTORY_FILTERS = [
  { id: 'all', label: 'Alle' },
  { id: 'breakfast', label: 'Frühstück', icon: Coffee },
  { id: 'lunch', label: 'Mittag & Abend', icon: UtensilsCrossed },
  { id: 'drinks', label: 'Getränke', icon: GlassWater },
  { id: 'snacks', label: 'Snacks', icon: Cookie },
  { id: 'payments', label: 'Zahlungen', icon: Banknote },
  { id: 'cancelled', label: 'Storniert', icon: RotateCcw },
];

const formatMoney = (value, signed = false) => {
  const amount = Number(value) || 0;
  const prefix = signed && amount > 0 ? '+' : '';
  return `${prefix}${amount.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
};

const formatTime = (value) => new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  hour: '2-digit',
  minute: '2-digit',
}).format(new Date(value));

const isTodayInBerlin = (value) => {
  const formatter = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(new Date(value)) === formatter.format(new Date());
};

const normalizeUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const getDepartmentLabel = (departmentId, departments) => {
  const department = departments.find(item => item.id === departmentId);
  if (department?.name) return department.name;
  const match = departmentId?.match(/abteilung(\d+)$/);
  return match ? `${match[1]}. Wachabteilung` : departmentId || '';
};

const getEntryPresentation = (entry) => {
  if (entry.type === 'payment') {
    return {
      title: entry.amount >= 0 ? 'Einzahlung' : 'Auszahlung',
      icon: Banknote,
      amount: Number(entry.amount) || 0,
      positive: entry.amount >= 0,
    };
  }

  if (entry.order_type === 'coffee_flat') {
    return {title: entry.title || 'Kaffee-Monatsflat', icon: Coffee, amount: -(Number(entry.total_price) || 0), positive: Number(entry.total_price) < 0};
  }
  if (entry.order_type === 'drinks') {
    return { title: 'Getränke', icon: GlassWater, amount: -Math.abs(calculateEmployeeOrderTotal(entry)) };
  }
  if (entry.order_type === 'sweets') {
    return { title: 'Snacks', icon: Cookie, amount: -Math.abs(calculateEmployeeOrderTotal(entry)) };
  }
  if (entry.order_type === 'category') {
    return {
      title: entry.visual_category_name || entry.menu_category_name || 'Weitere Artikel',
      icon: ReceiptText,
      amount: -Math.abs(calculateEmployeeOrderTotal(entry)),
    };
  }
  if (entry.order_type === 'deletion') {
    return { title: 'Stornierung', icon: RotateCcw, amount: Math.abs(calculateEmployeeOrderTotal(entry)) };
  }

  const categories = getHistoryCategories(entry);
  let title = 'Frühstück';
  if (categories.includes('breakfast') && categories.includes('lunch')) title = 'Frühstück & Mahlzeit';
  else if (categories.includes('lunch')) title = 'Mittag- oder Abendessen';

  return { title, icon: UtensilsCrossed, amount: -Math.abs(calculateEmployeeOrderTotal(entry)) };
};

const InlinePayPalLink = ({ paymentLink, label, compact = false }) => {
  if (!paymentLink) return null;
  return (
    <a
      href={normalizeUrl(paymentLink)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${label} mit PayPal bezahlen`}
      className={`inline-flex flex-none items-center gap-1.5 rounded-md border border-[#0070ba]/40 bg-white font-semibold text-[#003087] transition-colors hover:border-[#0070ba] hover:bg-[#f5fbff] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0070ba] focus-visible:ring-offset-2 ${
        compact ? 'min-h-8 px-2 py-1 text-[11px]' : 'min-h-9 px-3 py-1.5 text-xs'
      }`}
    >
      <WalletCards className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden="true" />
      <span>Mit <strong className="font-bold">PayPal</strong> bezahlen</span>
    </a>
  );
};

const BalanceValue = ({ label, value, paymentLink }) => {
  const amount = Number(value) || 0;
  return (
    <div className="min-w-0 rounded-xl bg-slate-100 px-4 py-3">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <p className={`text-2xl font-semibold tabular-nums tracking-tight ${
          amount < 0 ? 'text-red-700' : amount > 0 ? 'text-emerald-700' : 'text-slate-900'
        }`}>
          {formatMoney(amount)}
        </p>
        {amount < 0 && (
          <InlinePayPalLink paymentLink={paymentLink} label={label} />
        )}
      </div>
    </div>
  );
};

const OrderDetails = ({
  entry,
  employee,
  adminMode,
  cancellationState,
  confirming,
  cancelling,
  onCancelConfirm,
  onCancelDismiss,
}) => (
  <div className="border-t border-slate-100 px-4 pb-4 pt-3 sm:pl-[4.5rem] sm:pr-5">
    {entry.is_cancelled && (
      <div className="mb-3 flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
        <RotateCcw className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
        <p>
          {entry.cancellation_reason === 'reset_for_edit' ? 'Zum Bearbeiten zurückgesetzt' : 'Storniert'}
          {entry.cancelled_by_name ? ` von ${entry.cancelled_by_name}` : ''}
          {entry.cancelled_at ? ` am ${new Date(entry.cancelled_at).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })}` : ''}
        </p>
      </div>
    )}

    {(entry.is_sponsored || entry.is_sponsor_order) && (
      <div className="mb-3 flex items-start gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
        <Check className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
        <p>{entry.sponsored_message || entry.sponsor_message || `Ausgegeben von ${entry.sponsored_by_name}`}</p>
      </div>
    )}

    {(entry.readable_items || []).length > 0 && (
      <div className="space-y-2">
        {entry.readable_items.map((orderItem, index) => {
          const selfSponsored = entry.is_sponsored
            && entry.sponsored_by_name
            && employee?.name
            && entry.sponsored_by_name === employee.name;
          const sponsored = !selfSponsored && !entry.is_sponsor_order && orderItem.is_sponsored;
          const toppingsSponsored = !selfSponsored && !entry.is_sponsor_order && orderItem.toppings_is_sponsored;
          const employeeItemTotal = Number(orderItem.employee_total_price);
          const originalItemTotal = Number(orderItem.total_price_value);
          const hasAdjustedTotal = Number.isFinite(employeeItemTotal)
            && Number.isFinite(originalItemTotal)
            && employeeItemTotal !== originalItemTotal;
          return (
            <div key={`${orderItem.category || 'item'}-${index}`} className="flex items-start justify-between gap-4 text-sm">
              <div className="min-w-0">
                <p className={sponsored ? 'text-slate-400 line-through' : 'text-slate-800'}>
                  {orderItem.description}
                </p>
                {orderItem.toppings && (
                  <p className={`mt-0.5 text-xs ${toppingsSponsored ? 'text-slate-400 line-through' : 'text-slate-500'}`}>
                    mit {orderItem.toppings}
                  </p>
                )}
                {orderItem.unit_price && (
                  <p className="mt-0.5 text-xs text-slate-400">{orderItem.unit_price}</p>
                )}
              </div>
              {orderItem.total_price && (
                <div className="flex-none text-right text-sm tabular-nums">
                  <p className={hasAdjustedTotal ? 'text-xs text-slate-400 line-through' : 'text-slate-700'}>
                    {orderItem.total_price}
                  </p>
                  {hasAdjustedTotal && (
                    <p className="mt-0.5 font-medium text-slate-700">{formatMoney(employeeItemTotal)}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}

    <OrderEditHistory entries={entry.edit_history} />

    {entry.notes?.trim() && (
      <div className="mt-3 border-l-2 border-amber-300 pl-3 text-sm text-slate-700">
        <p className="text-xs font-semibold text-slate-500">Sonderwünsche</p>
        <p className="mt-0.5">{entry.notes}</p>
      </div>
    )}

    {!entry.is_cancelled && cancellationState && !cancellationState.cancellable && (
      <p className="mt-4 flex items-start gap-2 text-xs text-slate-500">
        <CircleAlert className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden="true" />
        {cancellationState.reason}
      </p>
    )}

    {confirming && (
      <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3">
        <p className="text-sm font-semibold text-red-900">
          Bestellung wirklich {adminMode ? 'löschen' : 'stornieren'}?
        </p>
        <p className="mt-1 text-xs text-red-800">Der Betrag wird dem zugehörigen Konto wieder gutgeschrieben.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onCancelConfirm}
            disabled={cancelling}
            className="rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          >
            {cancelling ? (adminMode ? 'Wird gelöscht…' : 'Wird storniert…') : (adminMode ? 'Löschen' : 'Stornieren')}
          </button>
          <button
            type="button"
            onClick={onCancelDismiss}
            disabled={cancelling}
            className="rounded-md px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
          >
            Abbrechen
          </button>
        </div>
      </div>
    )}
  </div>
);

const PaymentDetails = ({ entry }) => (
  <div className="border-t border-slate-100 px-4 pb-4 pt-3 text-sm text-slate-600 sm:pl-[4.5rem] sm:pr-5">
    <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
      <div>
        <dt className="text-xs font-medium text-slate-400">Konto</dt>
        <dd className="mt-0.5 text-slate-700">
          {entry.payment_type === 'combined' ? 'Gesamtkonto' : entry.payment_type === 'breakfast' ? 'Essen' : 'Getränke & Snacks'}
        </dd>
      </div>
      {entry.admin_user && (
        <div>
          <dt className="text-xs font-medium text-slate-400">Gebucht durch</dt>
          <dd className="mt-0.5 text-slate-700">{entry.booked_by || entry.admin_user}</dd>
        </div>
      )}
      {entry.notes && (
        <div className="sm:col-span-2">
          <dt className="text-xs font-medium text-slate-400">Hinweis</dt>
          <dd className="mt-0.5 text-slate-700">{entry.notes}</dd>
        </div>
      )}
    </dl>
  </div>
);

const HistoryEntry = ({
  entry,
  employee,
  departments,
  expanded,
  onToggle,
  cancellationState,
  confirming,
  cancelling,
  adminMode,
  onAskCancel,
  onCancelConfirm,
  onCancelDismiss,
}) => {
  const presentation = getEntryPresentation(entry);
  const Icon = presentation.icon;
  const departmentLabel = getDepartmentLabel(entry.department_id, departments);
  const isCancelled = entry.is_cancelled || entry.order_type === 'deletion';

  return (
    <article className={`bg-white ${isCancelled ? 'opacity-75' : ''}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-3 px-4 py-3.5 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-500 sm:px-5"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 text-slate-600">
          <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className={`truncate text-sm font-semibold ${isCancelled ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
              {presentation.title}
            </span>
            {isCancelled && <span className="text-xs font-medium text-amber-700">Storniert</span>}
            {(entry.is_sponsored || entry.is_sponsor_order) && <span className="text-xs font-medium text-emerald-700">Ausgegeben</span>}
          </span>
          <span className="mt-0.5 block truncate text-xs text-slate-500">
            {formatTime(entry.timestamp)} Uhr{departmentLabel ? ` · ${departmentLabel}` : ''}
          </span>
        </span>
        <span className={`text-sm font-semibold tabular-nums ${
          presentation.amount > 0 ? 'text-emerald-700' : presentation.amount < 0 ? 'text-red-700' : 'text-slate-600'
        } ${isCancelled ? 'line-through' : ''}`}>
          {formatMoney(presentation.amount, true)}
        </span>
        {expanded
          ? <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden="true" />
          : <ChevronRight className="h-4 w-4 text-slate-400" aria-hidden="true" />}
      </button>

      {expanded && (entry.type === 'payment' ? (
        <PaymentDetails entry={entry} />
      ) : (
        <OrderDetails
          entry={entry}
          employee={employee}
          adminMode={adminMode}
          cancellationState={cancellationState}
          confirming={confirming}
          cancelling={cancelling}
          onAskCancel={onAskCancel}
          onCancelConfirm={onCancelConfirm}
          onCancelDismiss={onCancelDismiss}
        />
      ))}
    </article>
  );
};

const HistoryCategoryEntry = ({
  group,
  employee,
  departments,
  expanded,
  onToggle,
  cancellationChecks,
  confirmingOrderId,
  cancellingOrderId,
  adminMode,
  onAskCancel,
  onCancelConfirm,
  onCancelDismiss,
}) => {
  const total = group.entries.reduce(
    (sum, entry) => sum + (entry.is_cancelled ? 0 : getEntryPresentation(entry).amount),
    0
  );
  const Icon = group.key === 'meals'
    ? UtensilsCrossed
    : group.key === 'drinks'
      ? GlassWater
      : group.key === 'snacks'
        ? Cookie
        : ReceiptText;

  return (
    <article className="bg-white">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-3 px-4 py-3.5 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-500 sm:px-5"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 text-slate-600">
          <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-slate-900">{group.label}</span>
          <span className="mt-0.5 block text-xs text-slate-500">
            {group.entries.length} {group.entries.length === 1 ? 'Buchung' : 'Buchungen'} an diesem Tag
          </span>
        </span>
        <span className={`text-sm font-semibold tabular-nums ${
          total > 0 ? 'text-emerald-700' : total < 0 ? 'text-red-700' : 'text-slate-600'
        }`}>
          {formatMoney(total, true)}
        </span>
        {expanded
          ? <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden="true" />
          : <ChevronRight className="h-4 w-4 text-slate-400" aria-hidden="true" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-2 sm:p-3">
          <div className="overflow-hidden rounded-md border border-slate-200 bg-white divide-y divide-slate-200">
            {group.entries.map((entry, index) => {
              const presentation = getEntryPresentation(entry);
              const departmentLabel = getDepartmentLabel(entry.department_id, departments);
              const isCancelled = entry.is_cancelled || entry.order_type === 'deletion';
              const cancellationState = cancellationChecks[entry.id];
              return (
                <section key={entry.id || `${entry.timestamp}-${index}`} className="bg-white">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-2.5 text-xs">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <p className={`font-medium ${isCancelled ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                        {formatTime(entry.timestamp)} Uhr{departmentLabel ? ` · ${departmentLabel}` : ''}
                      </p>
                      {isCancelled && <span className="font-semibold text-amber-700">Storniert</span>}
                    </div>
                    <div className="flex flex-none items-center gap-2">
                      <p className={`font-semibold tabular-nums ${
                        isCancelled
                          ? 'text-slate-400 line-through'
                          : presentation.amount < 0
                            ? 'text-red-700'
                            : presentation.amount > 0
                              ? 'text-emerald-700'
                              : 'text-slate-600'
                      }`}>
                        {formatMoney(presentation.amount, true)}
                      </p>
                      {!isCancelled && cancellationState?.cancellable && confirmingOrderId !== entry.id && (
                        <button
                          type="button"
                          onClick={() => onAskCancel(entry)}
                          className="inline-flex min-h-7 items-center gap-1 rounded border border-red-200 px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                        >
                          <RotateCcw className="h-3 w-3" aria-hidden="true" />
                          {adminMode ? 'Löschen' : 'Stornieren'}
                        </button>
                      )}
                    </div>
                  </div>
                  <OrderDetails
                    entry={entry}
                    employee={employee}
                    adminMode={adminMode}
                    cancellationState={cancellationState}
                    confirming={confirmingOrderId === entry.id}
                    cancelling={cancellingOrderId === entry.id}
                    onAskCancel={() => onAskCancel(entry)}
                    onCancelConfirm={() => onCancelConfirm(entry)}
                    onCancelDismiss={onCancelDismiss}
                  />
                </section>
              );
            })}
          </div>
        </div>
      )}
    </article>
  );
};

const EmployeeHistoryModal = ({
  employee,
  onClose,
  currentDepartment,
  apiBase = '/api',
  adminMode = false,
  onOrderUpdate,
}) => {
  const { isSingleDepartment, loading: stationModeLoading } = useStationMode();
  const showOtherDepartments = !isSingleDepartment && !stationModeLoading && !employee.is_external_guest;
  const [profile, setProfile] = useState(null);
  const [allBalances, setAllBalances] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [departmentConfig, setDepartmentConfig] = useState({ use_combined_accounts: false });
  const [paypalSettings, setPaypalSettings] = useState({ enabled: false });
  const [paypalByDepartment, setPaypalByDepartment] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [balanceLoadError, setBalanceLoadError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(15);
  const [expandedEntries, setExpandedEntries] = useState(() => new Set());
  const [selectedAccount,setSelectedAccount]=useState(null);
  const [remoteBusy,setRemoteBusy]=useState(false);
  const [remoteError,setRemoteError]=useState('');
  const selectionVersion=useRef(0);
  const remote=selectedAccount?.detail;
  const selectedDepartmentId=selectedAccount?.localDepartmentId||currentDepartment?.department_id;
  const isCurrentAccount=!remote&&selectedDepartmentId===currentDepartment?.department_id;
  const chooseAccount=selection=>{selectionVersion.current++;setSelectedAccount(selection);setActiveFilter('all');setExpandedEntries(new Set());setVisibleCount(15);setRemoteError('');setRemoteBusy(false);};
  const loadRemotePage=async()=>{
    const version=selectionVersion.current;setRemoteBusy(true);setRemoteError('');
    try {const {data}=await axios.get(`${apiBase}/network/employees/${encodeURIComponent(employee.id)}/stations/${encodeURIComponent(selectedAccount.station)}/accounts/${encodeURIComponent(remote.account.department_id)}`,{params:{cursor:remote.next_cursor}});
      if(version===selectionVersion.current)setSelectedAccount(previous=>({...previous,detail:{...data,history:[...previous.detail.history,...data.history]}}));
    }catch(error){if(version===selectionVersion.current)setRemoteError('Weitere Buchungen konnten nicht geladen werden.');}
    finally{if(version===selectionVersion.current)setRemoteBusy(false);}
  };
  const [networkView, setNetworkView] = useState(false);
  const networkContentRef = useRef(null);
  const historyScrollRef = useRef(null);
  const [compactHeader, setCompactHeader] = useState(false);
  const [cancellationChecks, setCancellationChecks] = useState({});
  const [confirmingOrderId, setConfirmingOrderId] = useState(null);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [feedback, setFeedback] = useState('');
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const is8HService = !!employee.is_8h_service;
  const isTemporaryGuest = employee.isTemporary
    || (employee.department_id !== currentDepartment?.department_id && !is8HService);

  const canBookCoffeeFlat = isCurrentAccount && !employee.is_guest && !employee.is_external_guest && !employee.isTemporary
    && (!employee.department_id || employee.department_id === currentDepartment?.department_id || is8HService);

  const checkCancellableOrders = useCallback(async (profileData) => {
    if (adminMode) {
      setCancellationChecks(Object.fromEntries(
        (profileData.order_history || [])
          .filter(order => order.order_type !== 'coffee_flat' && !order.is_cancelled && order.id)
          .map(order => [order.id, { cancellable: true, reason: null }])
      ));
      return;
    }
    const activeOrders = (profileData.order_history || []).filter(
      order => order.order_type !== 'coffee_flat' && !order.is_cancelled && order.id && isTodayInBerlin(order.timestamp)
    );
    const checks = await Promise.all(activeOrders.map(async order => {
      try {
        const response = await axios.get(`${apiBase}/employee/${employee.id}/orders/${order.id}/cancellable`);
        return [order.id, response.data];
      } catch (error) {
        return [order.id, { cancellable: false, reason: 'Stornierbarkeit konnte nicht geprüft werden.' }];
      }
    }));
    setCancellationChecks(Object.fromEntries(checks));
  }, [adminMode, apiBase, employee.id]);

  const loadData = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setIsLoading(true);
    setLoadError('');
    setBalanceLoadError('');

    const departmentId = currentDepartment?.department_id;
    const requests = [
      axios.get(`${apiBase}/employees/${employee.id}/profile`),
      axios.get(`${apiBase}/employees/${employee.id}/all-balances`),
      departmentId ? axios.get(`${apiBase}/department-paypal-settings/${departmentId}`) : Promise.resolve({ data: { enabled: false } }),
      departmentId ? axios.get(`${apiBase}/department-config/${departmentId}`) : Promise.resolve({ data: {} }),
      axios.get(`${apiBase}/departments`),
    ];

    try {
      const [profileResult, balancesResult, paypalResult, configResult, departmentsResult] = await Promise.allSettled(requests);
      if (profileResult.status !== 'fulfilled') throw profileResult.reason;

      setProfile(profileResult.value.data);
      const balanceDataAvailable = balancesResult.status === 'fulfilled' && configResult.status === 'fulfilled';
      if (balanceDataAvailable) {
        setAllBalances(balancesResult.value.data);
        setDepartmentConfig(configResult.value.data);
      } else {
        setAllBalances(null);
        setDepartmentConfig({ use_combined_accounts: false });
        setBalanceLoadError('Der Kontostand konnte nicht geladen werden.');
      }
      const currentPaypalSettings = paypalResult.status === 'fulfilled'
        ? paypalResult.value.data
        : { enabled: false };
      setPaypalSettings(currentPaypalSettings);

      const loadedDepartments = departmentsResult.status === 'fulfilled'
        ? departmentsResult.value.data
        : [];
      setDepartments(loadedDepartments);

      if (is8HService && !isSingleDepartment && loadedDepartments.length > 0) {
        const departmentPaypalResults = await Promise.allSettled(
          loadedDepartments.map(department => (
            department.id === departmentId && paypalResult.status === 'fulfilled'
              ? Promise.resolve(paypalResult.value)
              : axios.get(`${apiBase}/department-paypal-settings/${department.id}`)
          ))
        );
        setPaypalByDepartment(Object.fromEntries(
          loadedDepartments.map((department, index) => [
            department.id,
            departmentPaypalResults[index].status === 'fulfilled'
              ? departmentPaypalResults[index].value.data
              : { enabled: false },
          ])
        ));
      } else {
        setPaypalByDepartment(departmentId ? { [departmentId]: currentPaypalSettings } : {});
      }
      checkCancellableOrders(profileResult.value.data);
      return { balanceDataAvailable };
    } catch (error) {
      setLoadError(error.response?.data?.detail || 'Der Verlauf konnte nicht geladen werden.');
      return { balanceDataAvailable: false };
    } finally {
      setIsLoading(false);
    }
  }, [apiBase, checkCancellableOrders, currentDepartment?.department_id, employee.id, is8HService, isSingleDepartment]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useLayoutEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const scrollX = window.scrollX, scrollY = window.scrollY;
    const savedBody = Object.fromEntries(['position', 'top', 'left', 'width'].map(key => [key, document.body.style[key]]));
    const previouslyFocused = document.activeElement;
    document.body.style.overflow = 'hidden';
    Object.assign(document.body.style, {position:'fixed', top:`-${scrollY}px`, left:`-${scrollX}px`, width:'100%'});
    closeButtonRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (event) => {
      if (event.defaultPrevented || (event.target instanceof Element && event.target.closest('[data-coffee-flat-dialog], [data-network-dialog], [data-profile-protection-dialog]'))) return;
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(dialogRef.current.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      )).filter(element => !element.closest('[hidden]'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      Object.assign(document.body.style, savedBody);
      if (window.scrollX !== scrollX || window.scrollY !== scrollY) window.scrollTo({left:scrollX, top:scrollY, behavior:'instant'});
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus({ preventScroll: true });
    };
  }, []);

  useLayoutEffect(() => {
    if (historyScrollRef.current) historyScrollRef.current.scrollTop = 0;
    setCompactHeader(false);
  }, [employee.id, isLoading]);

  useEffect(() => {
    setVisibleCount(15);
  }, [activeFilter]);

  const combinedHistory = useMemo(() => {
    if (!profile) return [];
    if(remote)return remote.history.map(entry=>({...entry,id:`remote-${entry.kind}-${entry.id}`,type:entry.kind==='payment'?'payment':'order',total_price:entry.amount_cents/100,amount:entry.amount_cents/100,readable_items:(entry.readable_items||[]).map(item=>typeof item==='string'?{description:item}:item)}));
    const belongs=entry=>entry.department_id===selectedDepartmentId||(!entry.department_id&&selectedDepartmentId===(employee.department_id||currentDepartment?.department_id));
    const orders=(profile.order_history||[]).filter(belongs).map(order=>({...order,type:'order'}));
    const payments=(profile.payment_history||[]).filter(belongs).map(payment=>({...payment,type:'payment'}));
    return [...orders,...payments].sort((a,b)=>new Date(b.timestamp)-new Date(a.timestamp));
  },[profile,remote,selectedDepartmentId,employee.department_id,currentDepartment?.department_id]);

  const historyFilters = useMemo(() => {
    const dynamic = [];
    const seen = new Set();
    combinedHistory.forEach(entry => {
      if (entry.order_type !== 'category') return;
      const id = entry.visual_category_id || entry.menu_category_id || entry.visual_category_name;
      const label = entry.visual_category_name || entry.menu_category_name || 'Weitere Artikel';
      const filterId = `catalog:${id || label}`;
      if (seen.has(filterId)) return;
      seen.add(filterId);
      dynamic.push({ id: filterId, label, icon: ReceiptText });
    });
    return [
      ...HISTORY_FILTERS.slice(0, 5),
      ...dynamic,
      ...HISTORY_FILTERS.slice(5),
    ];
  }, [combinedHistory]);

  const filterCounts = useMemo(() => Object.fromEntries(
    historyFilters.map(filter => [filter.id, filterHistoryEntries(combinedHistory, filter.id).length])
  ), [combinedHistory, historyFilters]);
  const filteredHistory = useMemo(
    () => filterHistoryEntries(combinedHistory, activeFilter),
    [activeFilter, combinedHistory]
  );
  const groupedHistoryCount = useMemo(() => (
    groupHistoryEntriesByDay(filteredHistory).reduce(
      (count, day) => count + groupHistoryEntriesByCategory(day.items).length,
      0
    )
  ), [filteredHistory]);
  const historyGroups = useMemo(() => {
    let remaining = visibleCount;
    return groupHistoryEntriesByDay(filteredHistory)
      .map(day => {
        const groupedItems = groupHistoryEntriesByCategory(day.items);
        const items = groupedItems.slice(0, Math.max(remaining, 0));
        remaining -= items.length;
        return { ...day, items };
      })
      .filter(day => day.items.length > 0);
  }, [filteredHistory, visibleCount]);

  const currentSubaccount = allBalances?.subaccount_balances?.[currentDepartment?.department_id];
  const baseUseCombined = is8HService || isTemporaryGuest
    ? !!currentSubaccount?.use_combined
    : !!departmentConfig.use_combined_accounts;
  const baseCombinedBalance = is8HService || isTemporaryGuest
    ? Number(currentSubaccount?.combined ?? currentSubaccount?.total) || 0
    : Number(profile?.employee?.combined_balance) || 0;
  const baseBreakfastBalance = is8HService || isTemporaryGuest
    ? Number(currentSubaccount?.breakfast) || 0
    : Number(profile?.breakfast_total) || 0;
  const baseDrinksBalance = is8HService || isTemporaryGuest
    ? Number(currentSubaccount?.drinks) || 0
    : Number(profile?.drinks_sweets_total) || 0;

  const localOptions=buildDepartmentAccounts(allBalances?.subaccount_balances||{},departments).map(a=>({department_id:a.id,department_name:a.name,use_combined:a.useCombined,combined_cents:Math.round(a.total*100),breakfast_cents:Math.round(a.breakfast*100),drinks_cents:Math.round(a.drinks*100)}));
  const homeOption=localOptions.find(a=>a.department_id===currentDepartment?.department_id);
  if(homeOption)Object.assign(homeOption,{use_combined:baseUseCombined,combined_cents:Math.round(baseCombinedBalance*100),breakfast_cents:Math.round(baseBreakfastBalance*100),drinks_cents:Math.round(baseDrinksBalance*100)});
  const selectedLocal=localOptions.find(a=>a.department_id===selectedDepartmentId);
  const shown=remote?.account||(!isCurrentAccount?selectedLocal:null);
  const useCombined=shown?shown.use_combined:baseUseCombined;
  const combinedBalance=shown?shown.combined_cents/100:baseCombinedBalance;
  const breakfastBalance=shown?shown.breakfast_cents/100:baseBreakfastBalance;
  const drinksBalance=shown?shown.drinks_cents/100:baseDrinksBalance;
  const stationNumber=currentDepartment?.department_id?.match(/^fw(\d+)abteilung/i)?.[1];
  const stationLabel=stationNumber?`Wache ${stationNumber}`:'Eigene Wache';
  const accountLabel=`${remote?`Wache ${selectedAccount.station}`:stationLabel} · ${remote?.account.department_name||getDepartmentLabel(selectedDepartmentId,departments)}`;
  const paymentLinkFor = (type, balance) => remote?'':getPayPalLink(isCurrentAccount?paypalSettings:paypalByDepartment[selectedDepartmentId], type, balance);

  const toggleEntry = (entryId) => {
    setExpandedEntries(current => {
      const next = new Set(current);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
    setConfirmingOrderId(null);
  };

  const cancelOrder = async (entry) => {
    setCancellingOrderId(entry.id);
    setFeedback('');
    try {
      const deleteUrl = adminMode
        ? `${apiBase}/department-admin/orders/${entry.id}?admin_user=Admin`
        : `${apiBase}/employee/${employee.id}/orders/${entry.id}`;
      await axios.delete(deleteUrl);
      setConfirmingOrderId(null);
      const refreshResult = await loadData({ quiet: true });
      if (adminMode && onOrderUpdate) onOrderUpdate();
      const successAction = adminMode ? 'gelöscht' : 'storniert';
      setFeedback(refreshResult.balanceDataAvailable
        ? `Bestellung ${successAction}. Der Kontostand wurde aktualisiert.`
        : `Bestellung ${successAction}. Der Kontostand konnte nicht neu geladen werden.`);
    } catch (error) {
      setFeedback(error.response?.data?.detail || `Die Bestellung konnte nicht ${adminMode ? 'gelöscht' : 'storniert'} werden.`);
    } finally {
      setCancellingOrderId(null);
    }
  };

  const externalDisplay = externalGuestDisplay(employee) || externalGuestDisplay(profile?.employee);
  const titleId = `employee-history-title-${employee.id}`;
  const dialogHeader = (
    <header
      data-employee-history-header
      className="flex flex-none items-start justify-between gap-4 bg-white bg-none px-4 py-4 text-slate-950 sm:px-6 sm:py-5"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 id={titleId} className="truncate text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl">
            {profile?.employee?.name || employee.name}
          </h2>
          {isCurrentAccount && <ProfileProtectionControl employeeId={employee.id} departmentId={currentDepartment?.department_id} apiBase={apiBase}/>}
          {is8HService && showOtherDepartments && (
            <span className="rounded border border-orange-200 bg-orange-50 px-2 py-0.5 text-xs font-semibold text-orange-800">
              Übergreifend
            </span>
          )}
          {(employee.is_guest || externalDisplay) && (
            <span className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-600">
              {externalDisplay?.label || 'Gast'}
            </span>
          )}
        </div>
        {profile&&!isLoading&&!loadError&&<div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div data-account-context>
            <p className="text-xs font-semibold text-slate-500">{networkView?'Konto auswählen':isCurrentAccount?(externalDisplay||isTemporaryGuest?'Gastkonto':'Stammkonto'):remote?'Unterkonto · Andere Wache':'Unterkonto · Eigene Wache'}</p>
            <p className="mt-1 text-sm font-medium text-slate-900">{accountLabel}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!isCurrentAccount&&!networkView&&<button onClick={()=>chooseAccount(null)} className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"><ArrowLeft size={16}/>Zurück</button>}
            <NetworkAccounts employee={employee} departmentId={currentDepartment?.department_id} apiBase={apiBase} contentTarget={networkContentRef} onViewChange={setNetworkView} localStationLabel={stationLabel} localAccounts={!balanceLoadError&&!employee.is_guest&&!employee.is_external_guest&&!isTemporaryGuest?(showOtherDepartments?localOptions:localOptions.filter(a=>a.department_id===currentDepartment?.department_id)):[]} headerMode onReturnHome={()=>chooseAccount(null)} onSelect={chooseAccount}/>
          </div>
        </div>}
        {externalDisplay&&<p className="mt-2 text-xs text-slate-500">Stammabteilung: {externalDisplay.department}</p>}
      </div>
      <button
        ref={closeButtonRef}
        type="button"
        onClick={onClose}
        aria-label="Verlauf schließen"
        className="flex h-10 w-10 flex-none items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>
    </header>
  );

  return createPortal(
    <div data-employee-history-overlay className="fixed inset-0 z-[100] h-[100dvh] overflow-hidden sm:p-4">
      <div
        className="absolute inset-0 bg-slate-950/55"
        aria-hidden="true"
        onMouseDown={onClose}
      />
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden bg-slate-50 shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:rounded-xl"
      >
        {profile && !isLoading && !loadError && !networkView && <div
          data-compact-account-header
          aria-hidden={!compactHeader}
          className={`absolute inset-x-0 top-0 z-30 flex items-center gap-3 bg-white px-4 py-3 shadow-sm transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none sm:px-6 ${compactHeader ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'}`}
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-950">{profile?.employee?.name || employee.name}</p>
            <p className="truncate text-xs text-slate-500">{accountLabel}</p>
          </div>
          <button type="button" tabIndex={compactHeader ? 0 : -1} aria-label="Zur Kontoübersicht und Zahlung"
            onClick={() => historyScrollRef.current?.scrollTo({top:0, behavior:window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'})}
            className="flex shrink-0 flex-wrap justify-end gap-x-5 gap-y-1 rounded-lg px-2 py-1 text-right hover:bg-slate-50">
            {balanceLoadError && !remote ? <span className="text-xs text-slate-500">Saldo nicht verfügbar</span> :
              (useCombined ? [['Saldo', combinedBalance]] : [['Essen', breakfastBalance], ['Getränke & Snacks', drinksBalance]]).map(([label, value]) =>
                <span key={label}><span className="block text-[10px] text-slate-500">{label}</span><span className={`block text-base font-semibold tabular-nums ${Number(value)<0?'text-red-700':'text-slate-900'}`}>{formatMoney(value)}</span></span>)}
          </button>
          <button type="button" tabIndex={compactHeader ? 0 : -1} onClick={onClose} aria-label="Profil schließen" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"><X size={18}/></button>
        </div>}
        {isLoading ? (
          <>
            {dialogHeader}
            <div className="flex flex-1 items-center justify-center p-8" role="status">
            <div className="w-full max-w-md animate-pulse space-y-4">
              <div className="h-5 w-40 rounded bg-slate-200" />
              <div className="h-20 rounded-lg bg-slate-200" />
              <div className="h-11 rounded bg-slate-200" />
              <div className="space-y-2">
                <div className="h-16 rounded bg-slate-200" />
                <div className="h-16 rounded bg-slate-200" />
              </div>
              <span className="sr-only">Verlauf wird geladen</span>
            </div>
            </div>
          </>
        ) : loadError || !profile ? (
          <>
            {dialogHeader}
            <div className="flex flex-1 items-center justify-center p-6">
            <div className="max-w-sm text-center">
              <CircleAlert className="mx-auto h-8 w-8 text-red-600" aria-hidden="true" />
              <h3 className="mt-3 text-base font-semibold text-slate-900">Verlauf nicht verfügbar</h3>
              <p className="mt-1 text-sm text-slate-600">{loadError}</p>
              <button
                type="button"
                onClick={() => loadData()}
                className="mt-4 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
              >
                Erneut versuchen
              </button>
            </div>
            </div>
          </>
        ) : (
          <div
            ref={historyScrollRef}
            onScroll={event => setCompactHeader(event.currentTarget.scrollTop > 180)}
            data-modal-scroll-region="true"
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-slate-100"
          >
            {dialogHeader}
            <div ref={networkContentRef} hidden={!networkView} />
            <div hidden={networkView}>
            {balanceLoadError&&!remote ? (
              <section className="sticky top-0 z-20 border-b border-slate-200 bg-white">
                <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
                  <p className="flex items-center gap-2 text-sm text-slate-600">
                    <CircleAlert className="h-4 w-4 flex-none text-amber-700" aria-hidden="true" />
                    {balanceLoadError}
                  </p>
                  <button
                    type="button"
                    onClick={() => loadData({ quiet: true })}
                    className="flex-none text-sm font-semibold text-slate-700 underline decoration-slate-300 underline-offset-4 hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
                  >
                    Neu laden
                  </button>
                </div>
              </section>
            ) : (
              <>
                <section
                  data-primary-balance
                  className="bg-white px-4 py-3 sm:px-6"
                  aria-label="Aktuelle Kontostände"
                >
                <div className="mx-auto grid max-w-4xl grid-cols-1 gap-3 sm:grid-flow-col sm:auto-cols-fr sm:grid-cols-none">
                  {useCombined ? (
                    <BalanceValue label="Aktueller Saldo" value={combinedBalance} paymentLink={paymentLinkFor('combined', combinedBalance)} />
                  ) : (
                    <>
                      <BalanceValue label="Essen" value={breakfastBalance} paymentLink={paymentLinkFor('breakfast', breakfastBalance)} />
                      <BalanceValue label="Getränke & Snacks" value={drinksBalance} paymentLink={paymentLinkFor('drinks', drinksBalance)} />
                    </>
                  )}
                </div>
                </section>
                {canBookCoffeeFlat && <div className="bg-white px-4 pb-3 sm:px-6"><div className="mx-auto max-w-4xl">
                  <CoffeeFlatControl employeeId={employee.id} departmentId={currentDepartment?.department_id} apiBase={apiBase} inline
                    onChanged={() => {loadData({quiet:true}); onOrderUpdate?.();}} />
                </div></div>}
              </>
            )}

              {isCurrentAccount&&<div className="mx-auto max-w-4xl px-4 sm:px-6"><BalanceDeadlineNotice employeeId={employee.id} departmentId={currentDepartment?.department_id} /></div>}
              <nav className="border-b border-slate-200 bg-white px-4 sm:px-6" aria-label="Verlauf filtern">
                <div className="employee-history-tabs mx-auto max-w-4xl overflow-x-auto">
                  <div className="flex min-w-max gap-5">
                    {historyFilters.map(filter => {
                      const active = activeFilter === filter.id;
                      return (
                        <button
                          key={filter.id}
                          type="button"
                          aria-pressed={active}
                          aria-label={`${filter.label}, ${filterCounts[filter.id] || 0} Einträge`}
                          onClick={() => setActiveFilter(filter.id)}
                          className={`border-b-2 py-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 ${
                            active
                              ? 'border-slate-900 text-slate-950'
                              : 'border-transparent text-slate-500 hover:text-slate-900'
                          }`}
                        >
                          {filter.label}
                          <span className="ml-1.5 text-xs tabular-nums text-slate-400">{filterCounts[filter.id] || 0}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </nav>

              {feedback && (
                <div className="flex-none border-b border-slate-200 bg-white px-4 py-2 sm:px-6" role="status">
                  <p className="mx-auto flex max-w-4xl items-center gap-2 text-sm text-slate-700">
                    <ReceiptText className="h-4 w-4 flex-none text-slate-500" aria-hidden="true" />
                    {feedback}
                  </p>
                </div>
              )}

              <main data-history-list className="px-4 py-5 sm:px-6 sm:py-6">
                <div className="mx-auto max-w-4xl">
                  {filteredHistory.length === 0 ? (
                    <div className="py-14 text-center">
                      <ReceiptText className="mx-auto h-8 w-8 text-slate-300" aria-hidden="true" />
                      <h3 className="mt-3 text-sm font-semibold text-slate-900">
                        {activeFilter === 'all' ? 'Noch keine Buchungen' : 'Keine passenden Buchungen'}
                      </h3>
                      <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
                        {activeFilter === 'all'
                          ? 'Bestellungen und Zahlungen erscheinen hier automatisch.'
                          : 'Für diesen Filter sind keine Einträge vorhanden.'}
                      </p>
                      {activeFilter !== 'all' && (
                        <button
                          type="button"
                          onClick={() => setActiveFilter('all')}
                          className="mt-4 text-sm font-semibold text-slate-700 underline decoration-slate-300 underline-offset-4 hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
                        >
                          Alle Buchungen anzeigen
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-7">
                      {historyGroups.map(group => (
                        <section key={group.key} aria-labelledby={`history-day-${group.key}`}>
                          <div className="mb-2 flex items-baseline justify-between gap-3">
                            <h3 id={`history-day-${group.key}`} className="text-sm font-semibold capitalize text-slate-900">
                              {group.label}
                            </h3>
                            <span className="text-xs tabular-nums text-slate-400">
                              {group.items.reduce((count, item) => (
                                count + (item.kind === 'category' ? item.entries.length : 1)
                              ), 0)} Buchungen
                            </span>
                          </div>
                          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
                            {group.items.map((item, index) => {
                              const entryKey = `${group.key}:${item.id || index}`;
                              if (item.kind === 'category') {
                                return (
                                  <HistoryCategoryEntry
                                    key={entryKey}
                                    group={item}
                                    employee={profile.employee}
                                    departments={departments}
                                    expanded={expandedEntries.has(entryKey)}
                                    onToggle={() => toggleEntry(entryKey)}
                                    cancellationChecks={remote?{}:cancellationChecks}
                                    adminMode={remote?false:adminMode}
                                    confirmingOrderId={confirmingOrderId}
                                    cancellingOrderId={cancellingOrderId}
                                    onAskCancel={entry => setConfirmingOrderId(entry.id)}
                                    onCancelConfirm={cancelOrder}
                                    onCancelDismiss={() => setConfirmingOrderId(null)}
                                  />
                                );
                              }

                              const entry = item.entry;
                              return (
                                <HistoryEntry
                                  key={entryKey}
                                  entry={entry}
                                  employee={profile.employee}
                                  departments={departments}
                                  expanded={expandedEntries.has(entryKey)}
                                  onToggle={() => toggleEntry(entryKey)}
                                  cancellationState={remote?undefined:cancellationChecks[entry.id]}
                                  confirming={confirmingOrderId === entry.id}
                                  cancelling={cancellingOrderId === entry.id}
                                  adminMode={remote?false:adminMode}
                                  onAskCancel={() => setConfirmingOrderId(entry.id)}
                                  onCancelConfirm={() => cancelOrder(entry)}
                                  onCancelDismiss={() => setConfirmingOrderId(null)}
                                />
                              );
                            })}
                          </div>
                        </section>
                      ))}

                      {visibleCount < groupedHistoryCount && (
                        <div className="text-center">
                          <button
                            type="button"
                            onClick={() => setVisibleCount(count => count + 15)}
                            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400 hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
                          >
                            Weitere {Math.min(15, groupedHistoryCount - visibleCount)} anzeigen
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {remote?.next_cursor&&<div className="mx-auto mt-5 max-w-4xl text-center"><button disabled={remoteBusy} onClick={loadRemotePage} className="rounded-lg bg-white px-4 py-3 text-sm font-medium text-slate-700">{remoteBusy?'Wird geladen …':'Ältere Buchungen laden'}</button>{remoteError&&<p role="alert" className="mt-2 text-sm text-red-700">{remoteError}</p>}</div>}
              </main>
            </div>
            </div>
        )}
      </section>
    </div>, document.body
  );
};

export default EmployeeHistoryModal;
